// eslint-disable-next-line no-unused-vars
import React, {useState, useEffect} from 'react';
import styled from 'styled-components';
import {FaDiscord} from 'react-icons/fa';

// -------------------- Farben & Konstanten --------------------
const BRAND_COLOR = '#5865f2'; // entspricht var(--brand-500)
const PRIMARY_BG = '#2f3136'; // entspricht var(--primary-700)
const WHITE = '#fff';
const BLACK_20 = 'rgba(0,0,0,0.2)';

// -------------------- Container (Widget) --------------------

const StyledDiv = styled.div`
  margin-top: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const WidgetContainer = styled.div`
  display: flex;
  flex-direction: column;

  font-family: 'Helvetica', 'Arial', sans-serif;
  color: ${WHITE};
  border-radius: 5px;
  overflow: hidden;
  box-shadow: 0 1px 0 ${BLACK_20};
  background-color: ${PRIMARY_BG};


  @media (max-width: 1400px) {
    width: 261px !important;
  }

  @media (max-width: 1200px) {
    width: 216px !important;
  }

  @media (max-width: 992px) {
    width: 696px !important;
  }
`;

// -------------------- Header --------------------
const WidgetHeader = styled.div`
  background-color: ${BRAND_COLOR};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 10px 20px;
`;

const HeaderIcon = styled.div`
  margin-right: 8px;
  font-size: 2rem;
`;

const ServerName = styled.div`
  flex: 1;
  font-weight: bold;
  font-size: 1rem;
`;

const MemberCount = styled.div`
  font-size: 0.9rem;
  opacity: 0.9;
`;

// -------------------- Body --------------------
const WidgetBody = styled.div`
  flex: 1;
  padding: 20px;
  background-color: ${PRIMARY_BG};
  overflow-y: auto;
  box-shadow: inset 0 1px 0 ${BLACK_20};
`;

// -------------------- Channels --------------------
const ChannelSection = styled.div`
  margin-bottom: 12px;
`;

const ChannelTitle = styled.h3`
  margin: 4px 0;
  font-size: 0.9rem;
  font-weight: bold;
`;

const ChannelList = styled.ul`
  list-style: none;
  padding-left: 0;
  margin: 0;
  max-height: 150px;
  overflow-y: auto;
  font-size: 0.9rem;
`;

const ChannelItem = styled.li`
  margin: 2px 0;
  opacity: 0.9;
`;

// -------------------- Members --------------------
const MembersSection = styled.div`
  margin-bottom: 12px;
`;

const MembersTitle = styled.h3`
  margin: 4px 0;
  font-size: 0.9rem;
  font-weight: bold;
`;

const MemberList = styled.ul`
  list-style: none;
  padding-left: 0;
  margin: 0;
  max-height: 150px;
  overflow-y: auto;
  font-size: 0.9rem;
`;

const MemberItem = styled.li`
  display: flex;
  align-items: center;
  margin: 6px 0;
  padding-left: 10px;
  opacity: 0.9;
`;

const AvatarWrapper = styled.div`
  position: relative;
  width: 16px;
  height: 16px;
  margin-right: 4px;
`;

const Avatar = styled.img`
  border-radius: 8px;
  width: 16px;
  height: 16px;
`;

// -------------------- Footer --------------------
const WidgetFooter = styled.div`
  background-color: ${PRIMARY_BG};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  min-height: 30px;
  padding: 6px 6px 6px 20px;
  box-shadow: 0 -1px 18px ${BLACK_20}, 0 -1px 0 ${BLACK_20};
`;

const Slogan = styled.span`
  font-weight: 500;
  color: #8a8e94;
  opacity: 0.5;
  flex: 1;
  display: inline-block;
  font-size: 0.75rem;
`;

const JoinButton = styled.a`
  background-color: rgba(255, 255, 255, 0.08);
  border: 1px solid #212325;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
  border-radius: 4px;
  width: 120px;
  height: 30px;
  color: ${WHITE};
  text-decoration: none;
  font-size: 0.8rem;
  font-weight: bold;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: opacity 0.25s ease-out;

  &:hover {
    opacity: 0.6;
  }
`;

// -------------------- Haupt-Komponente --------------------
function CustomDiscordWidget() {
  const [widgetData, setWidgetData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch('https://discord.com/api/guilds/925452666346303510/widget.json')
      .then(res => {
        if (!res.ok) {
          throw new Error(`Fehler beim Laden: ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        setWidgetData(data);
      })
      .catch(err => {
        setError(err.message);
      });
  }, []);

  if (error) {
    return <div style={{color: 'red'}}>Error: {error}</div>;
  }

  if (!widgetData) {
    return <div>Loading...</div>;
  }

  const {instant_invite, channels, members, presence_count} = widgetData;

  return (
    <StyledDiv>
      <WidgetContainer>
        {/* Header */}
        <WidgetHeader>
          <HeaderIcon>
            <FaDiscord />
          </HeaderIcon>
          <ServerName>{'Arcane'}</ServerName>
          <MemberCount>{presence_count} Members Online</MemberCount>
        </WidgetHeader>

        {/* Body */}
        <WidgetBody>
          <ChannelSection>
            <ChannelTitle>Channels</ChannelTitle>
            <ChannelList>
              {channels
                ?.sort((a, b) => a.position - b.position)
                .map(channel => (
                  <ChannelItem key={channel.id}>{channel.name}</ChannelItem>
                ))}
            </ChannelList>
          </ChannelSection>

          <MembersSection>
            <MembersTitle>Members Online</MembersTitle>
            <MemberList>
              {members?.map(member => (
                <MemberItem key={member.id}>
                  <AvatarWrapper>
                    <Avatar src={member.avatar_url} alt={member.username} />
                  </AvatarWrapper>
                  {member.username}
                </MemberItem>
              ))}
            </MemberList>
          </MembersSection>
        </WidgetBody>

        {/* Footer */}
        <WidgetFooter>
          <Slogan>Hangout with people who get it</Slogan>
          {instant_invite && (
            <JoinButton href={instant_invite} target="_blank" rel="noopener noreferrer">
              Join Discord
            </JoinButton>
          )}
        </WidgetFooter>
      </WidgetContainer>
    </StyledDiv>
  );
}

export default CustomDiscordWidget;
